import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/components/ui/avatar'
import { Button } from '@repo/ui/components/ui/button'
import { Input } from '@repo/ui/components/ui/input'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { authClient, useSession } from '@/lib/auth-client'

interface GeneralTabProps {
  onSignedOut?: () => void
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function GeneralTab({ onSignedOut }: GeneralTabProps) {
  const { data: session } = useSession()
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setName(session?.user?.name ?? '')
  }, [session?.user?.name])

  if (!session?.user) return null

  const handleSave = async () => {
    setSaving(true)
    const result = await authClient.updateUser({ name })
    setSaving(false)

    if (result.error) {
      toast.error(result.error.message || 'Unable to update your account')
      return
    }

    toast.success('Profile updated')
  }

  const handleSignOut = async () => {
    await authClient.signOut()
    onSignedOut?.()
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-4 border-b border-border/70 pb-5">
        <Avatar className="size-14 rounded-[6px] border border-border/70 bg-muted/50">
          <AvatarImage src={session.user.image || undefined} alt={session.user.name} />
          <AvatarFallback className="rounded-[6px] text-base font-medium text-foreground">
            {getInitials(session.user.name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 pt-1">
          <h3 className="truncate text-xl font-semibold">{session.user.name}</h3>
          <p className="truncate text-base text-muted-foreground">{session.user.email}</p>
        </div>
      </div>

      <div className="rounded-[4px] border border-border/70 bg-card">
        <div className="grid gap-3 border-b border-border/60 px-5 py-4 md:grid-cols-[1fr_auto_auto] md:items-center">
          <div>
            <div className="text-sm font-medium">Full name</div>
            <div className="text-sm text-muted-foreground">
              Displayed in operator-facing account areas.
            </div>
          </div>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="h-9 w-56 border-border/60 bg-white"
          />
          <Button
            size="sm"
            variant="ghost"
            className="cursor-pointer"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>

        <div className="grid gap-3 border-b border-border/60 px-5 py-4 md:grid-cols-[1fr_auto_auto] md:items-center">
          <div>
            <div className="text-sm font-medium">Email</div>
            <div className="text-sm text-muted-foreground">
              Primary sign-in and billing contact. Business details live in Profile.
            </div>
          </div>
          <div className="text-sm text-foreground/90">{session.user.email}</div>
          <Button size="sm" variant="ghost" disabled>
            Managed
          </Button>
        </div>

        <div className="grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-4">
          <div>
            <div className="text-sm font-medium">Sign out</div>
            <p className="text-sm text-muted-foreground">End the current session on this device.</p>
          </div>
          <Button size="sm" variant="outline" className="cursor-pointer" onClick={handleSignOut}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  )
}
